import { Injectable, OnDestroy } from '@angular/core';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import moment from 'moment-timezone';

import { TargetingRhpContextsService } from '../../TargetingShared/services/targeting-rhp-contexts.service';
import { InteractionModel } from '../../../Discover/Targeting/models/interaction-model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { InteractionApiModel } from '../../../api/targeting/model/interaction-api.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../Core/Users/models/user.model';
import { InteractionType } from '../../../api/targeting/enum/interaction-type.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { FilterTypeCheckboxModel } from '../../Filter/models/filter-type-checkbox.model';
import { FilterService } from '../../Filter/services/abstract-filter.service';
import { FilterTypeModel } from '../../Filter/models/abstract-filter-type.model';
import { FilterTypeDateQueryModel } from '../../Filter/models/filter-type-date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { InteractionViewApi } from '../../../api/targeting/interaction-view.api';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { PaginatorServiceInterface } from '../../Misc/services/paginator.service.interface';
import { SortOrderServiceInterface } from '../../Misc/services/sort-order.service.interface';
import { PageCriteriaModelInterface } from '../../Misc/models/page-criteria.model.interface';
import { InterationViewFiltersEnum } from '../enums/interation-view-filters.enum';
import { DistributionService } from '../../TargetingShared/services/distribution.service';
import { DistributionSummaryModel } from '../../TargetingShared/model/distribution-summary-model';
import { getDefaultDateInputOptionValuesModel } from '../../Forms/models/date-query-input-options.model';

enum InteractionViewSortFieldEnum {
    DATETIME = 'DATETIME'
}
/** This service doesn't support b/e filter searches, but rather collects all interactions relating to a single entity and applies filters locally */
@Injectable()
export class InteractionViewService extends FilterService implements OnDestroy, PaginatorServiceInterface, SortOrderServiceInterface {

    private readonly MAX_ROW_SIZE = 200; // @hack - fix in NEP-12995

    private entityType: EntityType;
    private entityType$: BehaviorSubject<EntityType> = new BehaviorSubject<EntityType>(null);

    private entityId: string;
    private entityId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    private loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

    private allInteractions: InteractionModel[] = [];
    private filteredInteractions: InteractionModel[] = [];

    // clamped version of interactions
    private displayInteractions: InteractionModel[] = [];
    private displayInteractions$: BehaviorSubject<InteractionModel[]> = new BehaviorSubject<InteractionModel[]>(this.displayInteractions);

    private interactionCount: number = 0;
    private interactionCount$: BehaviorSubject<number> = new BehaviorSubject<number>(this.interactionCount);

    private pageCriteria: PageCriteriaModelInterface = {page: 1, pageSize: 25};
    private pageCriteria$: BehaviorSubject<PageCriteriaModelInterface> = new BehaviorSubject<PageCriteriaModelInterface>(this.pageCriteria);

    private distributions: DistributionSummaryModel[] = [];

    page: number = 1;
    pageSize: number = 25;
    totalNumberOfPages: number = 1;
    pagingValidator: () => Observable<boolean>;

    users: UserModel[];

    protected interactionTypeFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        InterationViewFiltersEnum.interactionType,
        'Discover.interactionView.dialog.type',
        [
            {
                label: 'Discover.interactionView.type.email',
                value: InteractionType.ADHOC_EMAIL
            },
            {
                label: 'Discover.interactionView.type.message',
                value: InteractionType.ADHOC_MESSAGE
            },
            {
                label: 'Discover.interactionView.type.phone',
                value: InteractionType.ADHOC_PHONE
            },
            {
                label: 'Discover.interactionView.type.inquiry',
                value: InteractionType.ADHOC_INBOUND_INQUIRY
            },
            {
                label: 'Discover.interactionView.type.other',
                value: InteractionType.ADHOC_OTHER
            }
        ],
    );
    protected interactionDateFilter: FilterTypeDateQueryModel = new FilterTypeDateQueryModel(
        InterationViewFiltersEnum.interactionDate,
        'Discover.interactionView.dialog.dateTime',
        getDefaultDateInputOptionValuesModel()
    );
    protected createdUserFilter: FilterTypeCheckboxModel = new FilterTypeCheckboxModel(
        InterationViewFiltersEnum.interactionUser,
        'Discover.interactionView.dialog.createdUser',
        []
    );
    allFilters: FilterTypeModel[] = [
        this.interactionTypeFilter,
        this.interactionDateFilter,
        this.createdUserFilter
    ];

    private sortFields: LocalizedEnum[] = [
        {
            enumValue: InteractionViewSortFieldEnum.DATETIME,
            displayString: 'Discover.interactionView.sort.dateTime'
        }
    ];
    private sortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>(null);

    private sortField: LocalizedEnum;
    private sortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(null);

    private sortOrder: SortOrderEnum = SortOrderEnum.descending;
    private sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);

    private subscriptions: Subscription[] = [];

    constructor(
        public targetingRhpContextsService: TargetingRhpContextsService,
        public usersService: UsersService,
        public contactsPublicApi: ContactsPublicApi,
        public outletsPublicApi: OutletsPublicApi,
        public currentUserStateService: CurrentUserStateService,
        public messageService: MessageService,
        public interactionViewApi: InteractionViewApi,
        public localizationService: LocalizationService,
        private distributionService: DistributionService
    ) {
        super();
        this.setPageCriteria({page: 1, pageSize: 25}, false);

        this.subscriptions.push(
            this.currentUserStateService.getAccountUsers().subscribe(users => this.users = users),
            this.targetingRhpContextsService.getEntity().subscribe(entity => {
                if (!entity) {
                    this.allInteractions = [];
                    this.updateInteractions();
                    return;
                }

                if (this.entityId !== entity.id) {
                    this.entityId = entity.id;
                    this.entityId$.next(this.entityId);

                    this.entityType = entity.entityType;
                    this.entityType$.next(this.entityType);

                    this.getInteractionsFromApi();
                }
            })
        );

        this.setFilters(this.allFilters);
        this.sortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortFields[0]);
        this.sortOrder = SortOrderEnum.descending;
        this.sortOrder$.next(this.sortOrder);
    }

    getInteractionsFromApi() {
        this.clearFilters();
        this.setPageCriteria({page: 1, pageSize: 25}, true);
        this.loading$.next(true);

        const entityApi = this.entityType === EntityType.CONTACT
            ? this.contactsPublicApi : this.outletsPublicApi;

        entityApi.getInteractions(
            this.entityId,
            this.MAX_ROW_SIZE
        ).pipe(
            tap(items => this.allInteractions = items?.map(interaction => new InteractionModel(interaction))),
            switchMap(() => {
                return this.distributionService.getDistributionSummaries().pipe(
                    tap(dist => this.distributions = dist)
                );
            }),
            switchMap(_ => this.usersService.getUsers(this.allInteractions.map(item => item.createdUserId || item.sentByUserId)))
        ).subscribe({
            next: users => {
                this.users = users;
                // remove interactions when author can't be found in account user list
                this.allInteractions = this.allInteractions?.filter(interaction => {
                    const createdUserId = this.users.find(x => x.id === interaction.createdUserId);
                    const sentByUserId = this.users.find(x => x.id === interaction.sentByUserId);

                    return createdUserId || sentByUserId;
                });
                this.allInteractions?.forEach(x => this.updateDisplayNames(x));
                this.allInteractions?.forEach(x => this.updateDistributionLink(x));
                this.updateCreatedUserFilterOptions();
                this.updateInteractions();
                this.loading$.next(false);
            },
            error: () => {
                this.loading$.next(false);
            }
        });
    }

    /** Perform filtering, sorting & update display interactions */
    private updateInteractions() {
        this.filteredInteractions = this.allInteractions;
        this.filterInteractions();
        this.sortInteractions();

        this.setDisplayInteractions(this.filteredInteractions);
        this.setInteractionCount(this.filteredInteractions.length);
    }


    applyFilters() {
        super.applyFilters();
        this.updateInteractions();
    }

    /** update filtered interactions */
    filterInteractions() {
        this.filters.forEach(interactionFilter => {
            switch (interactionFilter.id) {
                case InterationViewFiltersEnum.interactionType:
                    this.filterByInteractionType(interactionFilter as FilterTypeCheckboxModel);
                    break;
                case InterationViewFiltersEnum.interactionDate:
                    this.filterByInteractionDate(interactionFilter as FilterTypeDateQueryModel);
                    break;
                case InterationViewFiltersEnum.interactionUser:
                    this.filterByCreatedUser(interactionFilter as FilterTypeCheckboxModel);
            }
        });
    }

    filterByInteractionType(interactionTypeFilter: FilterTypeCheckboxModel) {
        const filterValue = interactionTypeFilter.getValue();
        if (filterValue.length > 0) {
            let interactionTypes = interactionTypeFilter.options;
            filterValue.forEach(type => {
                interactionTypes = interactionTypes.filter(x => x.value !== type);
            });
            interactionTypes.forEach(type => {
                this.filteredInteractions = this.filteredInteractions.filter(x => x.type !== type.value);
            });
        }
    }

    filterByInteractionDate(interactionDateFilter: FilterTypeDateQueryModel) {
        const filterValue = interactionDateFilter.getValue();
        if (filterValue && filterValue.type !== DateQueryTypeEnum.ALL) {
            let start, end;
            if (filterValue.type === DateQueryTypeEnum.RELATIVE) {
                start = moment(new Date()).add(filterValue.relativeStartOffsetH * 60, 'minutes');
                end = moment(new Date());
            } else {
                start = filterValue.absoluteRange.start;
                end = filterValue.absoluteRange.end;
            }
            this.filteredInteractions = this.filteredInteractions.filter(x => x.date <= end && x.date >= start);
        }
    }

    filterByCreatedUser(createdUserFilter: FilterTypeCheckboxModel) {
        const filterValue = createdUserFilter.getValue();
        if (filterValue.length > 0) {
            let createdUsers = createdUserFilter.options;
            filterValue.forEach(createdUser => {
                createdUsers = createdUsers.filter(x => x.value !== createdUser);
            });
            createdUsers.forEach(createdUser => {
                this.filteredInteractions = this.filteredInteractions.filter(x => x.createdUserId !== createdUser.value);
            });
        }
    }

    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.sortInteractions();
            this.setDisplayInteractions(this.filteredInteractions);
        }
    }
    setSortField(field: any): void {
        if (field !== this.sortField) {
            this.sortField = field;
            this.sortField$.next(field);
            this.sortInteractions();
            this.setDisplayInteractions(this.filteredInteractions);
        }
    }

    /** Sort filtered interactions */
    sortInteractions() {
        if (!this.sortField) {
            return;
        }

        switch (this.sortField.enumValue) {
            case InteractionViewSortFieldEnum.DATETIME:
                if (this.sortOrder === SortOrderEnum.descending) {
                    this.filteredInteractions.sort((x, y) => y.date - x.date);
                } else {
                    this.filteredInteractions.sort((x, y) => x.date - y.date);
                }
                break;
        }
    }

    addInteraction(interaction: InteractionApiModel): Observable<void> {
        this.clearFilters();

        return this.interactionViewApi.createInteraction(interaction).pipe(
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Discover.interactionView.success.create'),
                });

                const newInteraction = new InteractionModel(interaction);
                this.updateDisplayNames(newInteraction);
                this.allInteractions.push(newInteraction);
                this.updateCreatedUserFilterOptions();
                this.updateInteractions();
            }),
            catchError((err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Discover.interactionView.error.create'),
                });
                throw err;
            })
        );
    }

    editInteraction(interaction: InteractionApiModel): Observable<void> {
        this.clearFilters();

        return this.interactionViewApi.updateInteraction(interaction.id, interaction).pipe(
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.interactionView.success.edit',
                });

                const editedInteraction = new InteractionModel(interaction);
                this.updateDisplayNames(editedInteraction);
                this.allInteractions.splice(this.allInteractions.findIndex(x => x.id === interaction.id), 1, editedInteraction);
                this.updateInteractions();
            }),
            catchError((err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.interactionView.error.edit',
                });
                throw err;
            })
        );
    }

    deleteInteraction(interaction: InteractionModel) {
        this.interactionViewApi.deleteInteraction(interaction.id).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.interactionView.success.delete',
                });
                this.allInteractions.splice(this.allInteractions.indexOf(interaction), 1);
                this.updateCreatedUserFilterOptions();
                this.updateInteractions();

            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.interactionView.error.delete',
                });
            }
        });
    }

    updateCreatedUserFilterOptions() {
        this.allInteractions.forEach(interaction => {
            const createNameMissing: boolean = !this.createdUserFilter.options.find(x => x.value === interaction.createdUserId);

            if (createNameMissing) {
                const createdUserId = this.users.find(x => x.id === interaction.createdUserId);
                if (createdUserId) {
                    this.createdUserFilter.options.push(
                        { label:  `${createdUserId.firstName}  ${createdUserId.lastName}`, value: interaction.createdUserId }
                    );
                }

            }
        });
    }

    updateDisplayNames(interaction: InteractionModel) {
        const createdUserId = this.users.find(x => x.id === interaction.createdUserId);
        if (createdUserId) {
            interaction.createdUsername = createdUserId.firstName + ' ' + createdUserId.lastName;
        }

        const sentByUserId = this.users.find(x => x.id === interaction.sentByUserId);
        if (sentByUserId) {
            interaction.createdUsername = sentByUserId.firstName + ' ' + sentByUserId.lastName;
        }
    }

    private updateDistributionLink(interaction: InteractionModel) {
        if (interaction.type === InteractionType.DISTRIBUTION) {
            const distributionSummary = this.distributions?.find(dist => dist.name === interaction.summary);
            if (distributionSummary) {
                interaction.distId = distributionSummary.id;
            }
        }
    }

    // PaginatorServiceInterface

    getItemCount(): Observable<number> {
        return this.interactionCount$;
    }

    getPageCriteria(): Observable<PageCriteriaModelInterface> {
        return this.pageCriteria$;
    }

    setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
    }

    setPageCriteria(pageCriteria: PageCriteriaModelInterface,  search: boolean = true): void {
        this.page = pageCriteria.page;
        this.pageSize = pageCriteria.pageSize;
        this.interactionCount$.next(this.interactionCount);
        if (this.page <= this.totalNumberOfPages && search) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            this.setDisplayInteractions(this.filteredInteractions);
        }
    }

    canSetPageCriteria(): Observable<boolean> {
        if (this.pagingValidator) {
            return this.pagingValidator();
        }
        return of(true);
    }

    setInteractionCount(count: number) {
        this.interactionCount = count;
        this.interactionCount$.next(this.interactionCount);
    }

    /** Clamp display interactions based on page number & page size */
    setDisplayInteractions(interactions: InteractionModel[]) {
        this.displayInteractions = interactions?.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
        this.displayInteractions$.next(this.displayInteractions);
    }

    getDisplayInteractions(): Observable<InteractionModel[]> {
        return this.displayInteractions$;
    }

    getEntityType(): Observable<EntityType> {
        return this.entityType$;
    }

    getEntityId(): Observable<string> {
        return this.entityId$;
    }

    getSortableFields(): Observable<any[]> {
        return this.sortFields$;
    }

    getSortField(): Observable<any> {
        return this.sortField$;
    }

    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }

    getLoading(): Observable<boolean> {
        return this.loading$;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
